import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Menu,
    MenuItem,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
} from "@mui/material";
import MUITooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { get, includes, orderBy } from "lodash";
import React, { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { Link } from "react-router-dom";
import BSInput from "../../../common/js/components/BSInput";
import Snackbar from "../../../common/js/components/KMSnackbar";
import LeaseAndSaleDropdownOptions from "../../../common/js/components/LeaseAndSaleDropdownOptions";
import StatusChip from "../../../common/js/components/StatusChip";
import { ExportIcon, List, ListingCircle, Map, Search, Sorty } from "../../../common/js/icons";
import RequestModalContainer from "../listing/RequestModalContainer";
import NameSearchModal from "../search/NameSearchModal";
import SavedSearchesModal from "../search/SavedSearchesModal";
import { navigate } from "../utils/navigate";
import { buildAddress, toDollarAmount, toSqft } from "../utils/operations";
import BrokerActiveListingsTable from "./BrokerActiveListings";
import ListingFilters from "./ListingFilters";
import ListingImportModal from "./ListingImportModal";
import "./Listings.css";
import ListingsWithMap from "./ListingsWithMap";
import ListingsWithoutMap from "./ListingsWithoutMap";
import ReportTitleModal from "./ReportTitleModal";
import schema, { buttonActions } from "./reviewListingsTables";

const StyledPageToolbar = styled(Grid)(() => ({
    marginBottom: "20px",
    marginTop: "20px",
}));

const StyledListingIdLink = styled("span")(() => ({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: 600,
}));

const StyledMenuSubItem = styled(MenuItem)(() => ({
    "&>a": {
        paddingLeft: "30px !important",
    },
}));

const StyledAlternateContent = styled("div")(() => ({
    margin: "auto",
    textAlign: "center",
    marginTop: "6em",
    marginBottom: "6em",
    color: "rgba(0, 0, 0, 0.30)",
    fontSize: "20px",
}));

const StyledLinkButton = styled(Link)(() => ({
    textDecoration: "none",
}));

const StyledSortable = styled("span")(() => ({
    paddingRight: "5px",
    paddingBottom: "3px",
}));

const StyledToplessInput = styled(BSInput)(() => ({
    marginTop: 0,
}));

const StyledListingNumber = styled("h4")(() => ({
    fontSize: "14px",
    margin: "0",
    borderColor: "#f1f1f1",
    color: "#2C99D2",
    fontWeight: 600,
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
}));

const StyledLoadingProgress = styled(CircularProgress)(() => ({
    marginRight: "5px",
    color: "grey",
}));

const BrokerListings = (props) => {
    const {
        activeSelectedListings,
        addSelectedToGroup,
        clearCurrentGroup,
        clearFilters,
        closeDeal,
        columnToSort,
        dataExport,
        deleteSelectedFromGroup,
        elementsSaving,
        errors,
        filterQuery,
        handleDataExport,
        handleDeleteCrumb,
        handleFilter,
        handleListingsSearchSubmit,
        handleListingTypeRadioClick,
        handleSearchCleared,
        handleSelectAllPropertyType,
        handleSelectMarket,
        handleSelectMicromarket,
        handleSelectSubmarket,
        handleSizeRadioClick,
        handleSort,
        handleStatusRadioClick,
        isLoading,
        loadedSearchCrumbs,
        listingCurrentGroup,
        listings,
        listingTab,
        mapCrumbCleared,
        marketFilterExpansionPanels,
        nameSaveModal,
        numListings,
        propertySearchFilters,
        propertyTypeExpansionPanels,
        role,
        searchCleared,
        searchCriteria,
        searchPolygon,
        searchResponse,
        searchRun,
        selectedListings,
        selectedListingsGroup,
        sortDirection,
        switchTab,
        toggleMarketFilterExpansionPanel,
        togglePropertyTypeExpansionPanel,
        toggleReportTitleModal,
        toggleRequestModal,
        updateFilter,
        updateSearchPolygon,
        viewSavedModal,
    } = props;

    const [closeDealDialogOpen, setCloseDealDialogOpen] = useState(false);
    const [listingToClose, setListingToClose] = useState({});
    const [selectAll, setSelectAll] = useState(true);
    const [selectAllGroup, setSelectAllGroup] = useState(true);
    const [selectAllActive, setSelectAllActive] = useState(true);
    const [mapDisplay, setMapDisplay] = useState(true);
    const [reportStyle, setReportStyle] = useState(null);
    const [reportDropdown, setReportDropdown] = useState(false);
    const [exportDropdown, setExportDropdown] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [listingDropdown, setListingDropdown] = useState(null);

    useEffect(() => {
        ReactGA.send({ hitType: "pageview", page: "/listings", title: "Broker Listings" });
    }, []);

    const handleShowModal = (spaceType, transactionType) => {
        setShowModal(!showModal);
        toggleRequestModal(spaceType, transactionType);
    };

    const handleSelectAll = () => {
        const checked = selectAll;
        props.handleSelectAll(!checked);
        setSelectAll(!checked);
    };

    const handleClickOpen = (listing) => {
        setCloseDealDialogOpen(true);
        setListingToClose(listing);
    };

    const handleCloseAndStart = async () => {
        const result = await closeDeal(listingToClose);
        if (result) {
            if (result.dealKey) {
                navigate(`/deal/${result.dealKey}`);
            }
        }
        setCloseDealDialogOpen(false);
        setListingToClose({});
    };

    const handleClose = () => {
        setCloseDealDialogOpen(false);
        setListingToClose({});
    };

    const toggleMap = (mapView) => {
        setMapDisplay(mapView);
    };

    const handleSelectReport = (reportStyle) => {
        setReportDropdown(null);
        setReportStyle(reportStyle);
        toggleReportTitleModal(true);
    };

    const handleReportDropdown = (event) => {
        setReportDropdown(event.currentTarget);
    };

    const handleReportDropdownClose = () => {
        setReportDropdown(null);
    };

    const handleExportDropdown = (event) => {
        setExportDropdown(event.currentTarget);
    };
    const handleExportDropdownClose = () => {
        setExportDropdown(null);
    };

    const handleExportAndClose = (selectionType, activeTab) => {
        setExportDropdown(null);
        handleDataExport(selectionType, activeTab);
    };

    const handleSelectAllGroup = () => {
        const checked = selectAllGroup;
        props.handleSelectAllGroup(!checked);
        setSelectAllGroup(!checked);
    };

    const handleSelectAllActive = () => {
        const checked = selectAllActive;
        props.selectAllActiveListings(!checked);
        setSelectAllActive(!checked);
    };

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleListingDropdown = (event) => {
        setListingDropdown(event.currentTarget.parentElement);
    };

    const handleCloseListingDropdown = () => {
        setListingDropdown(null);
    };

    const handleImportResponse = (result) => {
        if (result === true) {
            setSnackbarMessage("Success: Your file will now be processed.");
            setSnackbarVariant("success");
            setSnackbarOpen(true);
        } else if (result === false) {
            setSnackbarMessage("Failed: Columns must match Standard Import Template.");
            setSnackbarVariant("error");
            setSnackbarOpen(true);
        }
    };

    const listingCount = numListings && !elementsSaving[listingTab] ? numListings[listingTab] : 0;
    const listingz = listings[listingTab] ? listings[listingTab] : {};
    let disableDeleteFromGroup = true;
    const disclaimerText = `© 2019 - ${new Date().getFullYear()} Kidder Mathews Inc and its subsidiaries and/or assigns. The information supplied herein is from sources we deem reliable. It is provided without any representation, warranty, or guarantee, expressed or implied as to its accuracy. Prospective Buyer or Tenant should conduct an independent investigation and verification of all matters deemed to be material, including, but not limited to, statements of income and expenses. Consult your attorney, accountant, or other professional advisor. All information is proprietary to Kidder Mathews and intended for the sole purpose of providing consultancy services to clients and should not be reproduced, distributed or used for any other purpose nor divulged to third party data collection agencies as outlined by corporate policy.`;

    //sets disableDeleteFromGroup to false for Delete from Group button if no listings are selected
    let list = [];
    for (var [key, value] of selectedListingsGroup) {
        if (value && !list.includes(key)) {
            list.push(key);
        }
    }
    if (list.length !== 0) {
        disableDeleteFromGroup = false;
    }

    return (
        <div>
            <Collapse in={listingTab === "all_km_listings"} unmountOnExit style={{ marginLeft: -20, marginRight: -20 }}>
                <ListingFilters
                    role={role}
                    updateFilter={updateFilter}
                    searchSubmit={handleListingsSearchSubmit}
                    propertySearchFilters={propertySearchFilters}
                    handleSelectAllPropertyType={handleSelectAllPropertyType}
                    clearFilters={clearFilters}
                    searchCriteria={searchCriteria}
                    handleStatusRadioClick={handleStatusRadioClick}
                    handleSizeRadioClick={handleSizeRadioClick}
                    handleListingTypeRadioClick={handleListingTypeRadioClick}
                    errors={errors}
                    togglePropertyTypeExpansionPanel={togglePropertyTypeExpansionPanel}
                    propertyTypeExpansionPanels={propertyTypeExpansionPanels}
                    toggleMarketFilterExpansionPanel={toggleMarketFilterExpansionPanel}
                    marketFilterExpansionPanels={marketFilterExpansionPanels}
                    handleSelectMarket={handleSelectMarket}
                    handleSelectSubmarket={handleSelectSubmarket}
                    handleSelectMicromarket={handleSelectMicromarket}
                    handleSearchCleared={handleSearchCleared}
                    loadedSearchCrumbs={loadedSearchCrumbs}
                    handleDeleteCrumb={handleDeleteCrumb}
                />
            </Collapse>
            <Snackbar
                open={snackbarOpen}
                message={snackbarMessage}
                variant={snackbarVariant}
                vertical="top"
                horizontal="center"
                autoHideDuration={3000}
                onClose={() => {
                    setSnackbarOpen(false);
                }}
            />
            <StyledPageToolbar container sx={{ justifyContent: "space-between", alignItems: "center" }}>
                <Grid>
                    <h2 className="page-title">Listings</h2>
                    <div className="page-subtitle">
                        {listingTab !== "all_km_listings" &&
                            listingTab !== "listings_group" &&
                            `Showing ${listingCount} ${listingCount === 1 ? "listing" : "listings"} `}
                        {filterQuery > "" && listingTab !== "all_km_listings" && "(Filtered)"}
                        {listingTab === "all_km_listings" &&
                            searchResponse &&
                            searchResponse.results &&
                            `Showing  ${searchResponse.results.length} ${
                                searchResponse.results.length === 1
                                    ? "listing"
                                    : `listings (${searchResponse.totalResultCount} TOTAL)`
                            }`}

                        {listingTab === "listings_group" &&
                            listingCurrentGroup &&
                            `You have ${listingCurrentGroup.length} ${
                                listingCurrentGroup.length === 1
                                    ? "listing in your current list"
                                    : `listings in your current list`
                            }`}
                    </div>
                </Grid>
                <Grid>
                    {listingTab === "all_km_listings" || listingTab === "broker_open" ? (
                        <Button
                            disabled={false}
                            variant="contained"
                            color="primary"
                            onClick={(e) => addSelectedToGroup(listingTab)}
                        >
                            Add to List
                        </Button>
                    ) : listingTab === "listings_group" ? (
                        <React.Fragment>
                            <Button
                                disabled={disableDeleteFromGroup}
                                variant="contained"
                                color="primary"
                                onClick={deleteSelectedFromGroup}
                            >
                                Delete From List
                            </Button>
                            <Button
                                disabled={disableDeleteFromGroup}
                                variant="contained"
                                color="primary"
                                onClick={clearCurrentGroup}
                            >
                                Clear List
                            </Button>
                        </React.Fragment>
                    ) : (
                        ""
                    )}

                    {listingTab === "all_km_listings" &&
                    includes(["dataAdmin", "dataEntry", "research", "researchAdmin"], role) ? (
                        <React.Fragment>
                            <Button
                                disabled={false}
                                variant="contained"
                                color="primary"
                                onClick={() => handleOpenModal()}
                            >
                                Import
                            </Button>
                            <ListingImportModal
                                modalOpen={modalOpen}
                                closeModal={handleCloseModal}
                                importResponse={handleImportResponse}
                            />
                        </React.Fragment>
                    ) : (
                        ""
                    )}

                    {(listingTab === "all_km_listings" ||
                        listingTab === "listings_group" ||
                        listingTab === "broker_open") &&
                        (get(dataExport, "exportInProgress", false) === true ? (
                            <Button disabled={true} variant="outlined" color="primary">
                                <ExportIcon /> In Progress
                            </Button>
                        ) : (
                            <MUITooltip
                                title={disclaimerText}
                                slotProps={{
                                    popper: {
                                        modifiers: [
                                            {
                                                name: "offset",
                                                options: {
                                                    offset: [0, 75],
                                                },
                                            },
                                        ],
                                    },
                                }}
                                placement="left-end"
                            >
                                <span>
                                    <Button
                                        id="Export_Broker_Listing"
                                        aria-haspopup="true"
                                        variant="contained"
                                        onClick={handleExportDropdown}
                                        endIcon={<ArrowDropDownIcon />}
                                        disabled={Object.keys(listingz).length === 0}
                                    >
                                        Export
                                    </Button>
                                    <Menu
                                        id="Export_Broker_Listing_Menu"
                                        anchorEl={exportDropdown}
                                        open={Boolean(exportDropdown)}
                                        onClose={handleExportDropdownClose}
                                        anchorOrigin={{
                                            vertical: "bottom", // Align the top of the menu with the bottom of the button
                                            horizontal: "right", // Align the right edge of the menu with the right edge of the button
                                        }}
                                        transformOrigin={{
                                            vertical: "top", // Align the bottom of the menu with the top of the button
                                            horizontal: "right", // Align the left edge of the menu with the right edge of the button
                                        }}
                                    >
                                        <StyledMenuSubItem onClick={() => handleExportAndClose("selected", listingTab)}>
                                            Selected Listings Only
                                        </StyledMenuSubItem>
                                        <StyledMenuSubItem onClick={() => handleExportAndClose("all", listingTab)}>
                                            Entire Data Set
                                        </StyledMenuSubItem>
                                    </Menu>
                                </span>
                            </MUITooltip>
                        ))}

                    {(listingTab === "all_km_listings" ||
                        listingTab === "listings_group" ||
                        listingTab === "broker_open") &&
                        (elementsSaving.generateListingReport ? (
                            <React.Fragment>
                                <Button disabled={true} variant="contained" color="secondary">
                                    <StyledLoadingProgress size={15} />
                                    Generating
                                </Button>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <MUITooltip
                                    title={disclaimerText}
                                    slotProps={{
                                        popper: {
                                            modifiers: [
                                                {
                                                    name: "offset",
                                                    options: {
                                                        offset: [0, 60],
                                                    },
                                                },
                                            ],
                                        },
                                    }}
                                    placement="left-end"
                                >
                                    <span>
                                        <Button
                                            id="Generate_Broker_Listing_Report"
                                            aria-haspopup="true"
                                            onClick={handleReportDropdown}
                                            endIcon={<ArrowDropDownIcon />}
                                            color="primary"
                                            variant="contained"
                                            disabled={Object.keys(listingz).length === 0}
                                        >
                                            Generate Report
                                        </Button>
                                        {reportDropdown && (
                                            <Menu
                                                id="Generate_Broker_Listing"
                                                anchorEl={reportDropdown}
                                                open={Boolean(reportDropdown)}
                                                onClose={handleReportDropdownClose}
                                                anchorOrigin={{
                                                    vertical: "bottom", // Align the top of the menu with the bottom of the button
                                                    horizontal: "right", // Align the right edge of the menu with the right edge of the button
                                                }}
                                                transformOrigin={{
                                                    vertical: "top", // Align the bottom of the menu with the top of the button
                                                    horizontal: "right", // Align the left edge of the menu with the right edge of the button
                                                }}
                                            >
                                                <StyledMenuSubItem onClick={() => handleSelectReport("Standard")}>
                                                    Standard
                                                </StyledMenuSubItem>
                                                <StyledMenuSubItem onClick={() => handleSelectReport("MapOverview")}>
                                                    Map Overview
                                                </StyledMenuSubItem>
                                                <StyledMenuSubItem
                                                    onClick={() => handleSelectReport("HalfMap-HalfProperty")}
                                                >
                                                    Half Map / Half Property List
                                                </StyledMenuSubItem>
                                                <StyledMenuSubItem
                                                    onClick={() => handleSelectReport("IndividualDetail")}
                                                >
                                                    Individual Listing Page Detail
                                                </StyledMenuSubItem>
                                                <StyledMenuSubItem onClick={() => handleSelectReport("Reference")}>
                                                    Property Reference
                                                </StyledMenuSubItem>
                                            </Menu>
                                        )}
                                    </span>
                                </MUITooltip>
                            </React.Fragment>
                        ))}

                    {includes(
                        [
                            "research",
                            "researchAdmin",
                            "broker",
                            "portlandBrokerAdmin",
                            "arizonaBrokerAdmin",
                            "evp",
                            "accounting",
                            "propertyManagement",
                            "transition",
                            "general",
                            "officeManager",
                            "appraiser",
                        ],
                        role
                    ) && (
                        <React.Fragment>
                            <Button
                                id="Request-Listing-Dropdown"
                                aria-controls={Boolean(listingDropdown) ? "Request-Listing-Menu" : undefined}
                                aria-haspopup="true"
                                aria-expanded={Boolean(listingDropdown) ? "true" : undefined}
                                variant="contained"
                                onClick={handleListingDropdown}
                                endIcon={<ArrowDropDownIcon />}
                            >
                                Request Listing
                            </Button>
                            <Menu
                                id="Request-Listing-Menu"
                                anchorEl={listingDropdown}
                                open={Boolean(listingDropdown)}
                                onClose={handleCloseListingDropdown}
                                anchorOrigin={{
                                    vertical: "bottom", // Align the top of the menu with the bottom of the button
                                    horizontal: "right", // Align the right edge of the menu with the right edge of the button
                                }}
                                transformOrigin={{
                                    vertical: "top", // Align the bottom of the menu with the top of the button
                                    horizontal: "right", // Align the left edge of the menu with the right edge of the button
                                }}
                                sx={{
                                    maxHeight: "80vh", // Set a max height for the dropdown
                                    overflowY: "none", // Enable vertical scrolling if items exceed max height
                                    "& .MuiMenuItem-root": {
                                        paddingRight: "5vw", // Add padding to all MenuItems
                                    },
                                }}
                            >
                                <LeaseAndSaleDropdownOptions callback={handleShowModal} />
                            </Menu>
                        </React.Fragment>
                    )}
                    <RequestModalContainer
                        {...props}
                        handleShowModal={handleShowModal}
                        showModal={showModal}
                        modalType="listing"
                    />
                </Grid>
            </StyledPageToolbar>
            <div style={{ display: "flex" }}>
                <div className="simPaper" style={{ flex: "1 1 auto" }}>
                    <div id={"listingTabsLine"} style={{ display: "flex", justifyContent: "space-between" }}>
                        <div>
                            {includes(["broker", "portlandBrokerAdmin", "arizonaBrokerAdmin", "evp"], role) ? (
                                <Tabs value={listingTab} onChange={switchTab}>
                                    <Tab value="broker_requested" label="My Listing Requests" />
                                    <Tab value="broker_open" label="My Active Listings" />
                                    <Tab value="all_km_listings" label="All Listings" />
                                    <Tab
                                        value="listings_group"
                                        label={`Current List (${
                                            listingCurrentGroup ? listingCurrentGroup.length : "0"
                                        })`}
                                    />
                                </Tabs>
                            ) : includes(
                                  [
                                      "general",
                                      "officeManager",
                                      "research",
                                      "researchAdmin",
                                      "propertyManagement",
                                      "transition",
                                      "accounting",
                                      "appraiser",
                                  ],
                                  role
                              ) ? (
                                <Tabs value={listingTab} onChange={switchTab}>
                                    <Tab value="broker_requested" label="My Listing Requests" />
                                    <Tab value="all_km_listings" label="All Listings" />
                                    <Tab
                                        value="listings_group"
                                        label={`Current List (${
                                            listingCurrentGroup ? listingCurrentGroup.length : "0"
                                        })`}
                                    />
                                </Tabs>
                            ) : (
                                <Tabs value={listingTab} onChange={switchTab}>
                                    <Tab value="all_km_listings" label="All Listings" />
                                    <Tab
                                        value="listings_group"
                                        label={`Current List (${
                                            listingCurrentGroup ? listingCurrentGroup.length : "0"
                                        })`}
                                    />
                                </Tabs>
                            )}
                        </div>
                        {listingTab === "broker_requested" && (
                            <div>
                                <StyledToplessInput
                                    value={filterQuery}
                                    placeholder="Search listings"
                                    errorPlaceholder="hideIt"
                                    startAdornment={<Search />}
                                    onChange={(event) => {
                                        handleFilter(event.target.value);
                                    }}
                                />
                            </div>
                        )}

                        {(listingTab === "all_km_listings" || listingTab === "listings_group") && (
                            <div>
                                {/* <Grid container> */}
                                {mapDisplay ? (
                                    <div>
                                        <Button style={{ minWidth: "40px" }} onClick={() => toggleMap(false)}>
                                            <List />
                                        </Button>
                                    </div>
                                ) : (
                                    <div>
                                        <Button style={{ minWidth: "40px" }} onClick={() => toggleMap(true)}>
                                            <Map />
                                        </Button>
                                    </div>
                                )}
                                {/* </Grid> */}
                            </div>
                        )}
                    </div>

                    {listingTab === "all_km_listings" || listingTab === "listings_group" ? (
                        mapDisplay ? (
                            <ListingsWithMap
                                {...props}
                                selectAll={selectAll}
                                selectAllGroup={selectAllGroup}
                                searchRun={searchRun}
                                searchCleared={listingTab === "listings_group" ? false : searchCleared}
                                handleSelectAll={handleSelectAll}
                                handleSelectAllGroup={handleSelectAllGroup}
                                handleSearchCleared={handleSearchCleared}
                                handleSearchSubmit={handleListingsSearchSubmit}
                                selectedListings={selectedListings}
                                mapCrumbCleared={mapCrumbCleared}
                                searchPolygon={searchPolygon}
                                updateSearchPolygon={updateSearchPolygon}
                            />
                        ) : (
                            <ListingsWithoutMap
                                {...props}
                                selectAll={selectAll}
                                selectAllGroup={selectAllGroup}
                                searchRun={searchRun}
                                searchCleared={listingTab === "listings_group" ? false : searchCleared}
                                handleSelectAll={handleSelectAll}
                                handleSelectAllGroup={handleSelectAllGroup}
                                handleSearchCleared={handleSearchCleared}
                                handleSearchSubmit={handleListingsSearchSubmit}
                                selectedListings={selectedListings}
                                openCloseDealDialog={handleClickOpen}
                            />
                        )
                    ) : listingTab === "broker_open" ? (
                        <BrokerActiveListingsTable
                            {...props}
                            selectAll={selectAllActive}
                            searchRun={searchRun}
                            searchCleared={searchCleared}
                            handleSelectAll={handleSelectAllActive}
                            handleSearchCleared={handleSearchCleared}
                            handleSearchSubmit={handleListingsSearchSubmit}
                            selectedListings={activeSelectedListings}
                            openCloseDealDialog={handleClickOpen}
                        />
                    ) : (
                        ""
                    )}

                    {listingTab === "broker_requested" && (
                        <div style={{ display: "flex" }}>
                            <Table>
                                <TableHead>
                                    <TableRow style={{ borderBottom: "1px solid rgb(212, 212, 212)" }}>
                                        {schema.viewHeaders[listingTab].map((x, i) => (
                                            <TableCell key={`thc-${i}`}>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                    }}
                                                    onClick={() => handleSort(x.sort ? x.sort : x.prop)}
                                                >
                                                    <StyledSortable>{x.name}</StyledSortable>
                                                    {x.name && (
                                                        <div>
                                                            <Sorty />
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                {!isLoading && (
                                    <TableBody>
                                        {Object.keys(listingz).map((propKey, i) =>
                                            row(
                                                propKey,
                                                role,
                                                listingTab,
                                                isLoading,
                                                columnToSort,
                                                sortDirection,
                                                listingz,
                                                filterQuery
                                            )
                                        )}
                                    </TableBody>
                                )}
                            </Table>
                        </div>
                    )}

                    {listingTab === "broker_requested" ? (
                        elementsSaving[listingTab] ? (
                            <StyledAlternateContent>
                                <CircularProgress color="primary" />
                            </StyledAlternateContent>
                        ) : Object.keys(listingz).length === 0 ? (
                            <StyledAlternateContent>No listings</StyledAlternateContent>
                        ) : (
                            ""
                        )
                    ) : (
                        ""
                    )}
                </div>
            </div>
            <Dialog
                open={closeDealDialogOpen}
                onClose={handleClose}
                aria-labelledby="close-deal"
                aria-describedby="close-deal-dialog"
            >
                <DialogTitle id="close-deal">{"Are you sure?"}</DialogTitle>
                {listingToClose.status === "Closed" ? (
                    <React.Fragment>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                A deal has already been started for this listing. It is likely that this happened
                                recently and the system has not picked up the change yet.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose} color="primary" variant="outlined">
                                Cancel
                            </Button>
                        </DialogActions>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                Closing this deal will start the invoice process for this transaction and move the
                                listing to a closed status. Portland Listings will be closed after the Comp for this
                                Deal has been Submitted and Approved.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose} color="primary" variant="outlined">
                                Cancel
                            </Button>
                            <Button onClick={handleCloseAndStart} color="secondary" autoFocus variant="contained">
                                Continue
                            </Button>
                        </DialogActions>
                    </React.Fragment>
                )}
            </Dialog>
            <ReportTitleModal {...props} reportType="Listing" tab={listingTab} reportStyle={reportStyle} />
            <SavedSearchesModal {...props} searchType="listings" tab={listingTab} showModal={viewSavedModal} />
            <NameSearchModal {...props} searchType="listings" tab={listingTab} showModal={nameSaveModal} />
        </div>
    );
};

const row = (propKey, role, listingTab, isLoading, columnToSort, sortDirection, listingz, filterQuery) => {
    const sortedData = orderBy(
        filterQuery && listingz && listingz[propKey]
            ? listingz[propKey].filter((x) => schema.filterByQuery(x, schema.columnsToQueryFilter, filterQuery))
            : listingz[propKey],
        columnToSort,
        sortDirection
    );

    return isLoading ? null : (
        <React.Fragment>
            {sortedData.length > 0 && (
                <TableRow>
                    <TableCell colSpan={7}>
                        <div style={{ display: "flex", alignItems: "center", gap: "1vw" }}>
                            <div>
                                <ListingCircle />
                            </div>
                            <div>
                                <div className="cluster">
                                    <span className="clusterHeader">
                                        {get(sortedData[0], "property.propertyAttributes.propertyName", null)
                                            ? get(sortedData[0], "property.propertyAttributes.propertyName", "")
                                            : get(sortedData[0], "property.primaryAddress.address1", "")}
                                    </span>
                                    <span className="clusterSubHeader">
                                        {`${sortedData.length} ${sortedData.length === 1 ? "listing" : "listings"}`}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </TableCell>
                </TableRow>
            )}
            {sortedData.map((x, i) => {
                const buttonProperties = get(buttonActions, `${role}.${x.status}`, buttonActions.defaultAction);

                return (
                    <TableRow key={`tr-${i}`} selectable="false">
                        {schema.viewHeaders[listingTab].map((y, k) =>
                            y.name === "Address" ? (
                                <TableCell key={`trc-${k}`} style={{ position: "relative" }}>
                                    <StyledLinkButton to={`/listing/${x.listingKey}`}>
                                        <StyledListingNumber>{`No. ${get(x, "listingKey", "")}`}</StyledListingNumber>
                                        <StyledListingIdLink>{get(x, "listingName", "")}</StyledListingIdLink>
                                    </StyledLinkButton>
                                    <div>
                                        <span className="clusterSubHeader">
                                            {buildAddress(get(x, "property.primaryAddress", {}))}
                                        </span>
                                    </div>
                                </TableCell>
                            ) : y.transform ? (
                                <TableCell key={`trc-${k}`} align={y.numeric ? "right" : "left"}>
                                    {y.transform(get(x, y.prop))}
                                </TableCell>
                            ) : y.prop === "status" ? (
                                <TableCell>
                                    <StatusChip
                                        status={
                                            buttonProperties.statusToDisplay && buttonProperties.statusToDisplay !== ""
                                                ? buttonProperties.statusToDisplay
                                                : x.status
                                        }
                                    />
                                </TableCell>
                            ) : y.name === "Rate" ? (
                                <TableCell>
                                    {get(x, "listingType", null) === "Lease"
                                        ? `${toDollarAmount(get(x, "askingRentMax"))}`
                                        : `${toDollarAmount(get(x, "listPrice", "-"))}`}
                                </TableCell>
                            ) : y.name === "SQFT" ? (
                                <TableCell>
                                    {get(x, "listingType", null) === "Lease"
                                        ? get(x, "sfDivisibleTo", null)
                                            ? `${toSqft(get(x, "sfDivisibleTo"))} - ${toSqft(get(x, "sfAvail"))}`
                                            : `${toSqft(get(x, "sfAvail", "-"))}`
                                        : `${toSqft(get(x, "sfAvail", "-"))}`}
                                </TableCell>
                            ) : (
                                <TableCell key={`trc-${k}`} align={y.numeric ? "right" : "left"}>
                                    {get(x, y.prop)}
                                </TableCell>
                            )
                        )}

                        <TableCell>
                            <StyledLinkButton to={`/listing/${x.listingKey}`}>
                                <Button variant={buttonProperties.variant} color="primary" onClick={() => null}>
                                    {buttonProperties.text}
                                </Button>
                            </StyledLinkButton>
                        </TableCell>
                    </TableRow>
                );
            })}
        </React.Fragment>
    );
};

export default BrokerListings;
